import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  createTreatmentPlan,
  deleteTreatmentPlanById,
  getTreatmentPlanByPatient,
  updateTreatmentPlanById,
} from '@services/api/treatment-plan.api'
import { ITreatmentPlan } from '@dataTypes/treatmentPlan.types'

export const fetchTreatmentPlanbyPatientId = createAsyncThunk<
  ITreatmentPlan[],
  string,
  { rejectValue: string }
>(
  'api/charting/fetch/TreatmentPlanbyPatientId',
  async (patientId, { rejectWithValue }) => {
    try {
      if (patientId.length === 0) return []
      const response = await getTreatmentPlanByPatient(patientId)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      if (
        response.data &&
        'treatment_plan' in response.data &&
        Array.isArray(response.data.treatment_plan)
      ) {
        return response.data.treatment_plan as ITreatmentPlan[]
      }

      return rejectWithValue('Treatment plan details not found')
    } catch (err) {
      return rejectWithValue('Failed to fetch treatment plan')
    }
  },
)

export const addTreatmentPlan = createAsyncThunk<
  ITreatmentPlan,
  { treatmentPlan: ITreatmentPlan },
  { rejectValue: string }
>(
  'api/charting/add/TreatmentPlan',
  async ({ treatmentPlan }, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await createTreatmentPlan({
        ...treatmentPlan,
        created_by: user.user_id,
        updated_by: user.user_id,
      })

      if (response.statusCode !== 201) {
        return rejectWithValue(
          response.message || 'Failed to fetch treatment plan',
        )
      }
      if (
        response.data &&
        'treatment_plan' in response.data &&
        typeof response.data.treatment_plan === 'object'
      ) {
        // If the API returns an array, return the first plan, or adjust as needed
        if (Array.isArray(response.data.treatment_plan)) {
          return response.data.treatment_plan[0] as ITreatmentPlan
        }
        return response.data.treatment_plan as ITreatmentPlan
      }
      return rejectWithValue('Treatment plan not found in the response')
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to create treatment plan'
      return rejectWithValue(message)
    }
  },
)

export const updateTreatmentPlan = createAsyncThunk<
  ITreatmentPlan,
  { treatmentPlanId: string; treatmentPlan: Partial<ITreatmentPlan> },
  { rejectValue: string }
>(
  'api/charting/update/TreatmentPlan',
  async ({ treatmentPlanId, treatmentPlan }, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await updateTreatmentPlanById(treatmentPlanId, {
        ...treatmentPlan,
        updated_by: user.user_id,
      })
      if (response.statusCode !== 200) {
        return rejectWithValue(
          response.message || 'Failed to fetch treatment plan',
        )
      }
      if (
        response.data &&
        'treatment_plan' in response.data &&
        typeof response.data.treatment_plan === 'object'
      ) {
        // If the API returns an array, return the first plan, or adjust as needed
        if (Array.isArray(response.data.treatment_plan)) {
          return response.data.treatment_plan[0] as ITreatmentPlan
        }
        return response.data.treatment_plan as ITreatmentPlan
      }
      return rejectWithValue('Treatment plan not found in the response')
    } catch {
      return rejectWithValue('Failed to update treatment plan')
    }
  },
)

export const deleteTreatmentPlan = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/charting/delete/TreatmentPlan', async (planId, { rejectWithValue }) => {
  if (!planId) return rejectWithValue('Plan ID not found')
  try {
    const response = await deleteTreatmentPlanById(planId)
    if (response.statusCode !== 200) {
      return rejectWithValue(
        response.message || 'Failed to delete treatment plan',
      )
    }
    return planId
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to delete treatment plan'
    return rejectWithValue(message)
  }
})
